/**
 * Phones and tablets (2026-10-06, the user's decision): ClinSpaceXR is built for a VR headset
 * or a computer with a mouse and keyboard, so the entry portal tells handheld visitors so
 * instead of loading a room they cannot move around in. Pure, so it is unit tested.
 */

export interface DeviceSignals {
  userAgent: string;
  /** Whether any input can point precisely (a mouse or trackpad): `(any-pointer: fine)`. */
  finePointer: boolean;
  /** `navigator.maxTouchPoints`. */
  touchPoints: number;
  /**
   * Whether the browser can start an immersive VR session, once known. Apple Vision Pro's
   * Safari says it is a Mac and reports a touch screen without a fine pointer, as an iPad does;
   * an iPad cannot start VR, the headset can.
   */
  immersiveVr?: boolean;
}

/** Headset browsers are always let in, whatever their pointer reports. */
const HEADSET = /OculusBrowser|Quest|PicoBrowser|Pico Neo|Wolvic|Vision Pro/i;

/** Phones say so in their user agent. */
const PHONE = /iPhone|iPod|Android.+Mobile|Windows Phone/i;

/**
 * True for a phone or a tablet: a phone's user agent, or a touch screen with nothing that
 * points precisely (an iPad reports itself as a Mac, but has no mouse unless one is attached).
 * A laptop with a touch screen still has its trackpad, so it is never handheld.
 */
export function isHandheld(s: DeviceSignals): boolean {
  if (s.immersiveVr || HEADSET.test(s.userAgent)) return false;
  if (PHONE.test(s.userAgent)) return true;
  return s.touchPoints > 0 && !s.finePointer;
}

/**
 * Apple Vision Pro (2026-10-07): Safari on visionOS gives a Mac Safari user agent, and no Mac
 * browser can start immersive VR, so Safari that can is the headset. Its input is gaze and
 * pinch (transient pointers), with no controllers.
 */
export function isVisionOs(userAgent: string, immersiveVr: boolean): boolean {
  return (
    immersiveVr &&
    /Macintosh/.test(userAgent) &&
    /Version\/[\d.]+.*Safari/.test(userAgent) &&
    !/Chrome|CriOS|Edg|Firefox/.test(userAgent)
  );
}

/** The signals of the browser this runs in. */
export function deviceSignals(): DeviceSignals {
  return {
    userAgent: navigator.userAgent,
    finePointer: window.matchMedia?.('(any-pointer: fine)').matches ?? true,
    touchPoints: navigator.maxTouchPoints ?? 0,
  };
}
